// The two multi-select pickers on the board: who owns a task, and which
// vehicles, owners and drivers it is about.
//
// ONE ROUND TRIP PER INTERACTION, NOT ONE PER TICK
//
// The selection is staged locally and written once, when the popover closes.
// Saving on every checkbox looked simpler and was worse: each save reloads the
// board, the reload re-renders the row, and the popover the person is still
// using closes underneath them. Staging also means a task that gains three
// owners produces one activity row saying so, rather than three that have to be
// read backwards to work out what happened.
//
// Closing by any route commits: the Done button, Escape, and clicking away.
// Discarding on outside click is the behaviour that loses somebody's work
// without telling them, which is the one outcome worth engineering against.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Loader2, Search } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '../../lib/shadcn/popover'
import { Input } from '../../lib/shadcn/input'
import { Button } from '../../lib/shadcn/button'
import { cn } from '../../lib/shadcn/utils'

export type Choice = {
  value: string
  label: string
  sub?: string | null
  /** Offboarded, archived, dismissed: still choosable, shown as past tense. */
  inactive?: boolean
  /** A heading this choice sits under. Choices with no section come first. */
  section?: string
  /** Shown after the label in brackets, for a fact the operator needs. */
  hint?: string
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const s = new Set(a)
  return b.every((x) => s.has(x))
}

export function MultiSelect({
  choices,
  selected,
  onCommit,
  title,
  searchPlaceholder,
  disabled,
  children,
  align = 'start',
}: {
  choices: Choice[]
  selected: string[]
  onCommit: (next: string[]) => Promise<void>
  title: string
  searchPlaceholder: string
  disabled?: boolean
  children: React.ReactNode
  align?: 'start' | 'center' | 'end'
}) {
  const [open, setOpen] = useState(false)
  const [staged, setStaged] = useState<string[]>(selected)
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  // Read inside the commit path, so the commit sees the latest staging even
  // when the popover closes in the same tick as the last click.
  const stagedRef = useRef(staged)
  stagedRef.current = staged

  useEffect(() => {
    // Re-seeding while open would throw away what the person is doing whenever
    // the board refreshes behind them, so this only runs while closed.
    //
    // The sameSet guard matters more than it looks. `selected` is rebuilt by the
    // parent on every render (LinkCell maps its links into an array of keys), so
    // without a value comparison this effect would set state on every render
    // that changed the array's identity but not its contents. It happens to be
    // safe today only because setStaged re-renders this component and not its
    // parent, which is a timing detail nobody should have to know.
    if (!open && !sameSet(staged, selected)) {
      setStaged(selected)
      setQ('')
    }
  }, [open, selected, staged])

  const commit = async () => {
    const next = stagedRef.current
    if (sameSet(next, selected)) return
    setBusy(true)
    try {
      await onCommit(next)
    } finally {
      setBusy(false)
    }
  }

  // EVERY route out of this popover goes through here.
  //
  // 23 August 2026, found by driving the real component in a browser: the Done
  // button called setOpen(false) directly, which does NOT go through Radix's
  // onOpenChange, so the one route a person is most likely to take was the one
  // route that saved nothing. Escape and clicking away both worked, which is
  // the worst possible shape for a bug like this: it looks fine when you test
  // it the lazy way. There is now one way to close and it always commits.
  const close = () => {
    setOpen(false)
    void commit()
  }

  const sections = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const match = (c: Choice) =>
      !needle ||
      c.label.toLowerCase().includes(needle) ||
      (c.sub ?? '').toLowerCase().includes(needle)
    const bySection = new Map<string, Choice[]>()
    for (const c of choices) {
      if (!match(c) && !staged.includes(c.value)) continue
      const key = c.section ?? ''
      const list = bySection.get(key)
      if (list) list.push(c)
      else bySection.set(key, [c])
    }
    // What is already chosen comes first inside its section. Without this the
    // header says "4 selected" and every one of the four is somewhere below the
    // fold among 183 drivers, which reads as though nothing is selected.
    for (const list of bySection.values()) {
      list.sort((a, b) => Number(staged.includes(b.value)) - Number(staged.includes(a.value)))
    }
    return [...bySection.entries()]
  }, [choices, q, staged])

  const toggle = (value: string) =>
    setStaged((s) => (s.includes(value) ? s.filter((v) => v !== value) : [...s, value]))

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setOpen(true)
        else close()
      }}
    >
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent align={align} className="w-80 p-0">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <span className="text-xs font-medium">{title}</span>
          {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
          <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
            {staged.length} selected
          </span>
        </div>
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 pl-7 text-sm"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {sections.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">
              Nothing matches “{q}”.
            </p>
          )}
          {sections.map(([section, items]) => (
            <div key={section || 'none'}>
              {section && (
                <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {section}
                </div>
              )}
              {items.map((c) => {
                const on = staged.includes(c.value)
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => toggle(c.value)}
                    className="flex w-full items-start gap-2 px-3 py-1.5 text-left hover:bg-muted/60"
                  >
                    <span
                      className={cn(
                        'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        on
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input bg-background',
                      )}
                    >
                      {on && <Check className="h-3 w-3" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-1.5">
                        <span
                          className={cn(
                            'truncate text-sm',
                            c.inactive && 'text-muted-foreground line-through',
                          )}
                        >
                          {c.label}
                        </span>
                        {c.hint && (
                          <span className="shrink-0 text-[10px] text-muted-foreground">
                            {c.hint}
                          </span>
                        )}
                      </span>
                      {c.sub && (
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {c.sub}
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-border px-2 py-1.5">
          <button
            type="button"
            onClick={() => setStaged([])}
            className="rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
          >
            Clear all
          </button>
          <Button size="sm" className="h-7" onClick={close}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
